import * as React from "react";
import { cn } from "../lib/cn";

export interface ArtifactViewProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Left pane content — the rendered artifact (doc / sheet / canvas). */
  viewer: React.ReactNode;
  /** Right pane content — the AI sidekick stream + actions. */
  aiPanel: React.ReactNode;
  /** AI panel width in px. Default 320 (matches v18 mockup). */
  aiPanelWidth?: number;
}

/**
 * ArtifactView — STEP D on the v18 mockup.
 *
 * Splits the surface body into `viewer` (left, flex 1) + `aiPanel` (right,
 * fixed 320px). Used in chat-driven workspaces (Docs, Slides, Sheets,
 * Email composer, Canvas). Scaffolding only — internal scrolling, AI
 * stream cursor + ai-pulse dot are styled by the children (consult v18
 * mockup CSS for the canonical implementation).
 */
export const ArtifactView = React.forwardRef<HTMLDivElement, ArtifactViewProps>(
  ({ viewer, aiPanel, aiPanelWidth = 320, className, style, ...rest }, ref) => (
    <div
      ref={ref}
      className={cn("grid min-h-0 gap-4", className)}
      style={{
        gridTemplateColumns: `1fr ${aiPanelWidth}px`,
        ...style,
      }}
      {...rest}
    >
      <div className="overflow-auto rounded-md border border-hairline bg-card/65 px-5 py-4">
        {viewer}
      </div>
      <div className="flex flex-col gap-2 overflow-hidden rounded-md border border-hairline bg-card/55 px-4 py-3">
        {aiPanel}
      </div>
    </div>
  )
);
ArtifactView.displayName = "ArtifactView";
